import ApiService from '@/common/api.service'

import * as utils from '@/common/utils'
import * as security from '@/common/security'

const module = 'admin/usersModule'

function properties () {
  return {
    id: null,
    title: '',
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    password: '',
    repeat_password: '',
    account_type_id: null,
    access_level: null,
    partner: { id: null, name: null },
    agency: { id: null, name: null },
    partner_id: null,
    agency_id: null,
    clients: [],
    position: '',
    employee_no: '',
    cellphone: '',
    national_id: '',
    timezone: null,
    language: null,
    country: null,
    registered_by: '',
    regInvitedUser: false,
    photos: [],
    settings: {}
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties(),
    created_at: '',
    updated_at: ''
  }
}

function formDefaults () {
  return {
    account_types: [],
    partners: [],
    agencies: [],
    client_selection: [],
    countries: [],
    languages: [],
    timezones: []
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param user        Object
 *
 * @return Object
 */
function transform (user) {
  let token = null
  if (user.hasOwnProperty('token')) {
    token = user.token
  }

  const clientIds = user.clients.map(client => client.id)

  return {
    id: user.id,
    title: user.title,
    first_name: user.first_name,
    last_name: user.last_name,
    username: user.username,
    email: user.email,
    password: user.password,
    repeat_password: user.repeat_password,
    account_type_id: user.account_type_id,
    partner_id: user.partner_id,
    agency_id: user.agency_id,
    client_ids: clientIds,
    position: user.position,
    employee_no: user.employee_no,
    cellphone: user.cellphone,
    national_id: user.national_id,
    timezone: user.timezone,
    language: user.language,
    country: user.country,
    photos: user.photos,
    token: token
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: properties(),
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form state
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }
}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    return ApiService
      .get(`users/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request "create" form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFormCreate (context) {
    return ApiService
      .get(`users/create`)
      .then((response) => {
        let user = defaults()

        // Set the default timezone, language and country to
        // that of the logged in user.
        user.timezone = Object.keys(security.user().timezone)[0]
        user.language = Object.keys(security.user().language)[0]
        user.country = Object.keys(security.user().country)[0]

        context.commit('set', user)
        context.commit('setFormData', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request a record and "edit" form data from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchFormEdit (context, id) {
    return ApiService
      .get(`users/${id}/edit/`)
      .then((response) => {
        let user = response.data.data

        if (user.account_type !== null) {
          user.account_type_id = user.account_type.id
          user.access_level = user.account_type.access_level.level
        } else {
          user.account_type_id = null
          user.access_level = null
        }

        if (user.partner.id !== null) user.partner_id = user.partner.id
        if (user.agency.id !== null) user.agency_id = user.agency.id

        if (user.timezone !== null) user.timezone = Object.keys(user.timezone)[0]
        if (user.language !== null) user.language = Object.keys(user.language)[0]
        if (user.country !== null) user.country = Object.keys(user.country)[0]

        context.commit('set', user)
        context.commit('setFormData', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request data from API to get details
   * of a invited user.
   *
   * @param context Object
   *
   * @param token
   * @return Promise
   */
  fetchRegisteredUser (context, token) {
    return ApiService
      .get(`auth/registration/${token}`)
      .then((response) => {
        let user = defaults()
        let responseUserData = response.data.data

        if (responseUserData.account_type !== null) {
          user.account_type_id = responseUserData.account_type.id
          user.access_level = responseUserData.account_type.access_level
        } else {
          user.account_type_id = null
          user.access_level = null
        }

        if (responseUserData.timezones !== null) user.timezone = Object.keys(responseUserData.timezones)[0]
        if (responseUserData.languages !== null) user.language = Object.keys(responseUserData.languages)[0]
        if (responseUserData.countries !== null) user.country = Object.keys(responseUserData.countries)[0]

        user.photos = []
        user.email = responseUserData.email
        user.registered_by = responseUserData.registered_by
        context.commit('set', user)
        context.commit('setFormData', response.data.data)
        context.commit('setIsModified', false)

        return response
      })
      .catch((response) => {
        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    return ApiService
      .post(`users`, transform(context.getters['get']))
      .then((response) => {
        context.commit('clearNoneDefaults')
        utils.addRecentHistory(module, response.data.data)
        return response
      })
  },

  /**
   * POST (create) new record of a invited user through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @param token
   * @return Promise
   */
  postInvitedUser (context, token) {
    let user = context.getters['get']
    user.token = token

    return ApiService
      .post(`auth/registration`, transform(
        user
      ))
      .then((response) => {
        context.commit('reset')
        context.commit('resetForm')
        context.commit('setRegInvitedUser', true)
        return response
      })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  put (context) {
    const user = context.getters['get']
    return ApiService
      .put(`users/${user.id}`, transform(user))
      .then((response) => {
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * DELETE existing record through the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  delete (context) {
    let id = context.getters['get'].id
    return ApiService
      .delete(`users/${id}`)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries all account types the user
   * is allowed to view.
   *
   * @param context
   * @param params
   * @returns Promise
   */
  queryAccountTypes (context, params) {
    return ApiService
      .get(`account-types`, params)
      .then((response) => {
        context.commit('setAccountTypes', response.data.data)
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`users`, params)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) basic user details.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  queryUsersBasic (context, params) {
    return ApiService
      .get(`users/basic`, params)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  queryHistory (context, params) {
    return ApiService
      .get(`users/${params.user_id}/history`, params)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = Object.assign({}, data)
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = Object.assign({}, data)
  },

  /**
   * Sets the regInvitedUser to true or false.
   *
   * @param state Object
   * @param data
   */
  setRegInvitedUser (state, data) {
    state.data.regInvitedUser = data
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    utils.pushProp(state.data, name, value)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Sets the user form data.
   *
   * @param state Object
   * @param data  Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setFormData (state, data) {
    state.form = {
      account_types: data.account_types,
      partners: data.partners,
      agencies: data.agencies,
      client_selection: data.client_selection,
      countries: data.countries,
      languages: data.languages,
      timezones: data.timezones
    }
  },

  /**
   * Set the user's partner.
   *
   * @param state
   * @param partner {Object}
   */
  setPartner (state, partner) {
    state.data.partner_id = partner.id
    state.data.partner.id = partner.id
    state.data.partner.name = partner.name
  },

  /**
   * Clear the user's partner.
   *
   * @param state
   */
  clearPartner (state) {
    state.data.partner_id = null
    state.data.partner.id = null
    state.data.partner.name = null
  },

  /**
   * Set the user's agency id.
   *
   * @param state
   * @param agency {Object}
   */
  setAgency (state, agency) {
    state.data.agency_id = agency.id
    state.data.agency.id = agency.id
    state.data.agency.name = agency.name
  },

  /**
   * Set the user's agency.
   *
   * @param state
   */
  clearAgency (state) {
    state.data.agency_id = null
    state.data.agency.id = null
    state.data.agency.name = null
  },

  /**
   * Clears all user properties that can't be considered
   * to have "default" values.
   *
   * @param state
   */
  clearNoneDefaults (state) {
    state.data.id = null
    state.data.title = ''
    state.data.first_name = ''
    state.data.last_name = ''
    state.data.username = ''
    state.data.email = ''
    state.data.password = ''
    state.data.repeat_password = ''
    state.data.position = ''
    state.data.employee_no = ''
    state.data.cellphone = ''
    state.data.national_id = ''
    state.data.photos = []
    state.data.settings = {}
  },

  /**
   * Add a client to the array of clients.
   *
   * @param state
   * @param client
   */
  addClient (state, client) {
    state.data.clients.push(client)
  },

  /**
   * Clear all clients from the user.
   *
   * @param state
   */
  clearClients (state) {
    state.data.clients = []
  },

  /**
   * Remove a client from the user based on the client's id.
   *
   * @param state
   * @param clientId
   */
  removeClientById (state, clientId) {
    const clientIdx = state.data.clients.findIndex(client => client.id === clientId)
    if (clientIdx > -1) {
      state.data.clients.splice(clientIdx, 1)
    }
  },

  /**
   * Sets the account types in form data
   *
   * @param state
   * @param data
   */
  setAccountTypes (state, data) {
    state.form.account_types = data
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.original = properties()
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/administration/users/usersModule.js